<template>
  <div>
    <div :class="{ 'hidden lg:block': compact }">
      <button
        v-if="appearance === 'card'"
        type="button"
        class="size-8 rounded-full bg-white/80 flex items-center justify-center cursor-pointer transition-opacity hover:bg-white/90"
        :aria-label="t('ariaLabel')"
        @click.stop.prevent="handleClick"
      >
        <TIcon :icon="isFavourited ? ['fas', 'heart'] : ['far', 'heart']" />
      </button>
      <TButton
        v-else
        :outline="hasLabel"
        :variant="!hasLabel ? 'white' : undefined"
        :icon="!hasLabel"
        :size="hasLabel ? 'md' : iconButtonSize"
        @click.stop.prevent="handleClick"
      >
        <TIcon :icon="isFavourited ? ['fas', 'heart'] : ['far', 'heart']" /><span
          v-if="hasLabel"
          class="ml-2"
        >{{
          t('actions.save')
        }}</span>
      </TButton>
    </div>
    <div :class="{ 'block lg:hidden': compact, 'hidden': !compact }">
      <button
        class="size-8 rounded-full bg-white flex items-center justify-center cursor-pointer opacity-80 hover:opacity-100 transition"
        type="button"
        tabindex="0"
        @click.stop.prevent="handleClick"
      >
        <TIcon :icon="isFavourited ? ['fas', 'heart'] : ['far', 'heart']" />
      </button>
    </div>

    <TModalCritical
      v-if="hasMultipleCollections"
      v-model:visible="showModal"
      :title="t('selectCollection')"
      enable-scroll
    >
      <template #default="{ dismiss }">
        <div
          class="divide-y divide-gray-200 flex flex-col rounded-md border border-gray-200 overflow-hidden"
        >
          <button
            v-for="collection in favourites"
            :key="collection.Id"
            type="button"
            class="flex justify-between gap-2 items-center px-4 py-3 cursor-pointer hover:bg-gray-50"
            @click="addToCollection(collection.Id, dismiss)"
          >
            <span>
              <TIcon
                :icon="['far', 'heart']"
                class="mr-2"
              />
              {{ collection.CollectionName === 'Favourites' ? t('favourites') : collection.CollectionName }}
            </span>
            <TBadge
              v-if="collection.Items"
              variant="highlight"
            >
              {{ collection.Items.length }}
            </TBadge>
          </button>

          <button
            type="button"
            class="flex gap-2 items-center px-4 py-3 cursor-pointer hover:bg-gray-50"
            @click="createNewCollection(dismiss)"
          >
            <TIcon
              :icon="['fas', 'plus']"
              fixed-width
            />
            {{ t('favourites.createNewList') }}
          </button>
        </div>
      </template>
    </TModalCritical>

    <FavouritesCollectionModal
      v-if="newCollection"
      visible
      :rv-id="rvId"
      @close="newCollection = false"
    />
  </div>
</template>

<script setup lang="ts">
import type { ButtonSize } from '~/components/_tailwind/button/types'

const props = withDefaults(defineProps<{
  rvId: string
  hasLabel?: boolean
  iconButtonSize?: ButtonSize
  appearance?: 'card' | 'default'
  compact?: boolean
}>(), {
  iconButtonSize: 'md',
  appearance: 'default',
})

const { $captureError } = useNuxtApp()
const { t } = useI18n()
const { isLoggedIn } = useAuthentication()
const { isWebView } = usePlatform()
const { showLogin } = useLogin()
const { favourites, addRvToCollection, removeRvFromCollection, checkIfRvIsFavourited, findCollectionIdByRvId } = useFavouritesData()

const showModal = ref(false)
const newCollection = ref(false)
const isLoading = ref(false)

const hasMultipleCollections = computed(() => favourites.value.length > 1)
const isFavourited = computed(() => checkIfRvIsFavourited(props.rvId))

async function handleClick() {
  const collectionId = favourites.value.find((collection) => collection.CollectionName === 'Favourites')?.Id

  if (!isLoggedIn.value) {
    if (isWebView.value) {
      const cookiesJSON = {
        [props.rvId]: useCookie(props.rvId).value,
      }
      const appMessage = {
        successUrl: window.location.href,
        failureUrl: window.location.href,
        cookies: cookiesJSON,
      }
      messageAppToSignup(appMessage)
    }
    else {
      showLogin()
    }
  }
  else if (isFavourited.value && collectionId) {
    try {
      isLoading.value = true

      const collectionId = findCollectionIdByRvId(props.rvId)

      if (collectionId) {
        await removeRvFromCollection(collectionId)
      }
    }
    catch (_error) {
      $captureError(new Error('Error removing RV from collection'))
    }
    finally {
      isLoading.value = false
    }
  }
  else if (hasMultipleCollections.value) {
    showModal.value = true
  }
  else {
    try {
      isLoading.value = true

      await addRvToCollection(props.rvId)
    }
    catch (_error) {
      $captureError(new Error('Error add RV to collection'))
    }
    finally {
      isLoading.value = false
    }
  }
}

async function addToCollection(collectionId?: number, dismiss?: () => void) {
  if (!collectionId) {
    return
  }

  await addRvToCollection(props.rvId, collectionId)
  dismiss?.()
}

async function createNewCollection(dismiss: () => void) {
  dismiss()
  newCollection.value = true
}
</script>

<i18n lang="json">
{
  "en": {
    "favourites": "Favorites",
    "selectCollection": "Select a Favorites List",
    "ariaLabel": "Save to favorites"
  },
  "fr": {
    "favourites": "Favoris",
    "selectCollection": "Choisissez une liste de favoris",
    "ariaLabel": "Enregistrer dans les favoris"
  }
}
</i18n>
